import { z } from 'zod/v4';

/** The Lumi Agent's chat endpoint, below the gateway's base URL. Internal users only. */
export const LUMI_AGENT_CHAT_PATH = '/gateway/lumi-agent/chat';

/** Whether the Lumi Agent asks before a change (the default) or makes it right away. */
export const LumiAgentModeSchema = z.enum(['ask_before_changes', 'change_without_asking']);
export type LumiAgentMode = z.infer<typeof LumiAgentModeSchema>;

/** The page the user came from. A link can set it, so it is held to a route and an entity id. */
export const LumiAgentPageContextSchema = z.object({
    route: z.string().regex(/^\/[\w$/.~%-]{0,300}$/),
    entity: z
        .object({ type: z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/), id: z.string().regex(/^[\w:.-]{1,128}$/) })
        .optional(),
});
export type LumiAgentPageContext = z.infer<typeof LumiAgentPageContextSchema>;

/** What `useChat` posts. The server validates `messages` as AI SDK UI messages. */
export const LumiAgentChatRequestSchema = z.object({
    conversationId: z.uuid(),
    messages: z.array(z.unknown()).min(1).max(200),
    mode: LumiAgentModeSchema,
    today: z
        .object({
            date: z.iso.date(),
            timeZone: z.string().regex(/^[A-Za-z][\w+-]{0,31}(\/[\w+-]{1,32}){0,2}$/),
        })
        .optional()
        .catch(undefined),
    view: z.enum(['panel', 'page']).optional().catch(undefined),
});
export type LumiAgentChatRequest = z.infer<typeof LumiAgentChatRequestSchema>;

/** PDFs and images: the model reads these itself. */
export const LUMI_AGENT_MODEL_FILE_TYPES = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
] as const;
/** File types put into the code sandbox to compute over. */
export const LUMI_AGENT_SANDBOX_FILE_TYPES = [
    'text/csv',
    'text/plain',
    'application/json',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;
export type LumiAgentFileType =
    | (typeof LUMI_AGENT_MODEL_FILE_TYPES)[number]
    | (typeof LUMI_AGENT_SANDBOX_FILE_TYPES)[number];
export const LUMI_AGENT_MAX_FILE_BYTES = 20 * 1024 * 1024;
/** All files of one message together. */
export const LUMI_AGENT_MAX_MESSAGE_FILE_BYTES = 30 * 1024 * 1024;

/** The error text of a stream that fails after the answer started. */
export const LumiAgentStreamErrorSchema = z.enum(['run_failed', 'turn_timed_out']);
export type LumiAgentStreamError = z.infer<typeof LumiAgentStreamErrorSchema>;

/** Why the endpoint refuses a turn with a 429, as the code of its answer; the page says each in its own words. */
export const LumiAgentRefusalSchema = z.enum(['rate', 'monthly_budget', 'agent_monthly_budget', 'ai_off', 'agent_off']);
export type LumiAgentRefusal = z.infer<typeof LumiAgentRefusalSchema>;

/**
 * What the gateway hands the model when an MCP tool answers with an error, in place of the tool's own answer. The page
 * reads `toolFailed` to show the step as failed.
 */
export const LumiAgentToolFailureSchema = z.object({ toolFailed: z.literal(true), error: z.unknown() });
export type LumiAgentToolFailure = z.infer<typeof LumiAgentToolFailureSchema>;

/** On every tool part as `toolMetadata`, from the gateway's read-only set. Missing means a change. */
export type LumiAgentToolMetadata = { readOnly: boolean };

/**
 * A user message's: the page it was sent from, set once at send, so a retry or an approval round keeps it. An answer's:
 * the model that wrote it, and where it ran when the cluster says so.
 */
export const LumiAgentMessageMetadataSchema = z.object({
    pageContext: LumiAgentPageContextSchema.optional(),
    model: z.string().optional(),
    region: z.string().optional(),
});
export type LumiAgentMessageMetadata = z.infer<typeof LumiAgentMessageMetadataSchema>;
